import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  approvePurchaseOrderQuery,
  createGrnQuery,
  createPurchaseOrderQuery,
  listPurchaseOrdersQuery,
  postGrnQuery
} from "../../../Graphql/querries/education/platformQuery";
import {
  GraphQLCodePayload,
  GraphQLData,
  unwrapGraphQLOperation
} from "./educationGraphQLUtils";

export type ProcurementOrder = {
  id?: string;
  orderNumber?: string;
  documentType?: string;
  supplierId?: string;
  procurementStatus?: string;
  lines?: {
    stockItemId?: string;
    stockItemName?: string;
    quantity?: number;
    unit?: string;
  }[];
};

type ListPurchaseOrdersPayload = GraphQLCodePayload & {
  orders?: ProcurementOrder[];
};

type CreateGrnPayload = GraphQLCodePayload & {
  order?: { id?: string };
};

export const usePurchaseOrders = (input?: { page?: number; size?: number }) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["purchaseOrders", input?.page ?? 0],
    queryFn: async (): Promise<ProcurementOrder[]> => {
      const data = await fetchGraphQL<
        GraphQLData<ListPurchaseOrdersPayload, "listPurchaseOrders">,
        { input: object }
      >(listPurchaseOrdersQuery, { input: input ?? {} }, setAlert);
      const response = unwrapGraphQLOperation(data, "listPurchaseOrders");
      return response.code === API_RESPONSE_CODE.OK
        ? (response.orders ?? [])
        : [];
    }
  });
};

const useProcurementAction = (
  query: string,
  operation: "approvePurchaseOrder" | "postGrn"
) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useMutation({
    mutationFn: async (input: Record<string, string>) => {
      const data = await fetchGraphQL<
        GraphQLData<GraphQLCodePayload, typeof operation>,
        { input: Record<string, string> }
      >(query, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, operation);
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useCreatePurchaseOrder = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useMutation({
    mutationFn: async (input: {
      supplierId: string;
      lines: unknown[];
      purchaseRequestId?: string;
    }) => {
      const data = await fetchGraphQL<
        GraphQLData<GraphQLCodePayload, "createPurchaseOrder">,
        { input: object }
      >(createPurchaseOrderQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createPurchaseOrder");
      if (response.code !== API_RESPONSE_CODE.OK) {
        throw new Error(response.code);
      }
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const useApprovePurchaseOrder = () =>
  useProcurementAction(approvePurchaseOrderQuery, "approvePurchaseOrder");

export const useCreateGrn = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useMutation({
    mutationFn: async (input: { purchaseOrderId: string }) => {
      const data = await fetchGraphQL<
        GraphQLData<CreateGrnPayload, "createGrn">,
        { input: object }
      >(createGrnQuery, { input }, setAlert);
      const response = unwrapGraphQLOperation(data, "createGrn");
      if (response.code !== API_RESPONSE_CODE.OK || !response.order?.id) {
        throw new Error(response.code);
      }
      return response.order.id;
    },
    onSuccess: () => {
      setAlert({
        code: ALERT_BANNER_CODE.OK,
        severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS
      });
    }
  });
};

export const usePostGrn = () => useProcurementAction(postGrnQuery, "postGrn");
